import { CometChat } from "@cometchat/chat-sdk-javascript";
import type { CallLog } from "@cometchat/calls-sdk-javascript";
import { COMETCHAT_CONSTANTS } from "../AppConstants";
import * as sounds from "./sounds";

/**
 * Raw calling plumbing — Chat SDK for signalling (ring/accept/reject),
 * standalone Calls SDK for the actual media session. Deliberately does
 * NOT use the UI Kit's prebuilt call widgets: their internal listener
 * lifecycles proved unreliable (the caller was never told the callee
 * accepted), while this direct approach is the one proven to work.
 *
 * The media SDK is heavy, so it is fetched and initialised only when a
 * call actually connects.
 */

type CallsModule = typeof import("@cometchat/calls-sdk-javascript").CometChatCalls;

let callsModulePromise: Promise<CallsModule> | null = null;

function loadCalls(): Promise<CallsModule> {
  if (!callsModulePromise) {
    callsModulePromise = import("@cometchat/calls-sdk-javascript").then(
      async ({ CometChatCalls }) => {
        const appId = COMETCHAT_CONSTANTS.APP_ID || localStorage.getItem("appId") || "";
        const region = COMETCHAT_CONSTANTS.REGION || localStorage.getItem("region") || "";
        const settings = new CometChatCalls.CallAppSettingsBuilder()
          .setAppId(appId)
          .setRegion(region)
          .build();
        await CometChatCalls.init(settings);
        return CometChatCalls;
      }
    );
  }
  return callsModulePromise;
}

export async function placeCall(
  receiverId: string,
  receiverType: "user" | "group",
  audioOnly: boolean
): Promise<CometChat.Call> {
  const type = audioOnly ? CometChat.CALL_TYPE.AUDIO : CometChat.CALL_TYPE.VIDEO;
  const rType =
    receiverType === "user" ? CometChat.RECEIVER_TYPE.USER : CometChat.RECEIVER_TYPE.GROUP;
  return CometChat.initiateCall(new CometChat.Call(receiverId, type, rType));
}

export async function acceptCall(sessionId: string): Promise<CometChat.Call> {
  return CometChat.acceptCall(sessionId);
}

export async function declineCall(sessionId: string): Promise<void> {
  await CometChat.rejectCall(sessionId, CometChat.CALL_STATUS.REJECTED);
}

export async function cancelOutgoingCall(sessionId: string): Promise<void> {
  await CometChat.rejectCall(sessionId, CometChat.CALL_STATUS.CANCELLED);
}

export async function rejectBusy(sessionId: string): Promise<void> {
  await CometChat.rejectCall(sessionId, CometChat.CALL_STATUS.BUSY);
}

/* ---------- device permission preflight ----------
   The Calls SDK only asks for the mic/camera when the media session
   starts — AFTER the call has been placed/accepted. If the browser
   prompt was dismissed, the device is held by another app, or access is
   blocked at the OS level, the SDK just showed a cryptic "not enabled"
   state mid-call. Requesting (and immediately releasing) the devices
   up-front, inside the user's click, surfaces problems BEFORE anyone's
   phone rings, with an error message that says what to actually do. */

export type MediaAccess =
  | { ok: true; cameraAvailable: boolean }
  | { ok: false; message: string };

function friendlyMediaError(e: unknown, audioOnly: boolean): string {
  const name = (e as { name?: string } | null)?.name || "";
  const what = audioOnly ? "microphone" : "camera and microphone";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return `Your ${what} is blocked for this site — click the padlock in the address bar, allow it, then try again.`;
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return `No ${what} was found on this device.`;
  }
  if (name === "NotReadableError" || name === "AbortError") {
    return `Your ${what} is busy in another app — close it and try again.`;
  }
  return `Couldn't access your ${what} — check the browser's site permissions.`;
}

export async function ensureMediaAccess(audioOnly: boolean): Promise<MediaAccess> {
  const md = navigator.mediaDevices;
  if (!md?.getUserMedia) {
    return { ok: false, message: "This browser can't access the microphone here — calls need a secure (https) page." };
  }
  const grab = async (video: boolean) => {
    const stream = await md.getUserMedia({ audio: true, video });
    stream.getTracks().forEach((t) => t.stop()); // probe only — release instantly
  };
  try {
    await grab(!audioOnly);
    return { ok: true, cameraAvailable: !audioOnly };
  } catch (e) {
    if (!audioOnly) {
      // Video call but the camera is blocked/missing/held: a working mic is
      // still a valid way to join — degrade instead of refusing outright.
      try {
        await grab(false);
        return { ok: true, cameraAvailable: false };
      } catch (audioErr) {
        return { ok: false, message: friendlyMediaError(audioErr, true) };
      }
    }
    return { ok: false, message: friendlyMediaError(e, audioOnly) };
  }
}

/* ---------- cross-tab coordination ----------
   Every open tab holds its own CometChat connection, so an incoming call
   used to ring — audibly — in ALL of them at once, and answering in one
   tab left the others ringing at a call that no longer existed. Two
   mechanisms fix that:
   - a localStorage claim so only ONE tab plays the ringtone (they all
     still show the answer card — any tab can pick up);
   - a BroadcastChannel "this session was handled" ping so the moment one
     tab accepts/declines, every other tab drops its card silently. */

const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const RING_CLAIM_TTL_MS = 90_000;

export function claimRing(sessionId: string): boolean {
  try {
    const key = `dbm-ring-claim:${sessionId}`;
    const raw = localStorage.getItem(key);
    if (raw) {
      const [owner, ts] = raw.split("|");
      if (owner !== TAB_ID && Date.now() - Number(ts) < RING_CLAIM_TTL_MS) return false;
    }
    localStorage.setItem(key, `${TAB_ID}|${Date.now()}`);
    return true;
  } catch {
    return true; // storage unavailable — better to ring than stay silent
  }
}

export function releaseRing(sessionId: string): void {
  try {
    const key = `dbm-ring-claim:${sessionId}`;
    if (localStorage.getItem(key)?.startsWith(`${TAB_ID}|`)) localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

type HandledListener = (sessionId: string) => void;
const handledListeners = new Set<HandledListener>();
let callBus: BroadcastChannel | null = null;
try {
  callBus = new BroadcastChannel("dbm-call-bus");
  callBus.onmessage = (e: MessageEvent) => {
    const d = e.data;
    if (d?.type === "call-handled" && typeof d.sessionId === "string") {
      handledListeners.forEach((fn) => fn(d.sessionId));
    }
  };
} catch {
  callBus = null; // very old browser — tabs just behave independently
}

/** Tell every other tab this session is dealt with (answered/declined/busy). */
export function announceCallHandled(sessionId: string): void {
  releaseRing(sessionId);
  try {
    callBus?.postMessage({ type: "call-handled", sessionId });
  } catch {
    /* ignore */
  }
}

export function onCallHandledElsewhere(fn: HandledListener): () => void {
  handledListeners.add(fn);
  return () => {
    handledListeners.delete(fn);
  };
}

/** Busy rejection (callee on another call) vs an actual "no". */
export function wasBusy(call: CometChat.Call): boolean {
  try {
    return call.getStatus() === CometChat.CALL_STATUS.BUSY;
  } catch {
    return false;
  }
}

export interface SessionHandlers {
  onEnded: () => void;
  onError: (message: string) => void;
  /** Someone else dropped out. `remaining` counts other participants still
   * connected, so a 1:1 call can end itself the moment it hits zero. */
  onPeerLeft?: (remaining: number) => void;
}

/* The "someone joined" chime is meant only as confirmation that a person
   YOU added via "Add people" has arrived — not a sound every participant
   hears every time anyone connects (which fired even on a plain 1:1 answer
   or a normal group-call join). We record when this client last sent an
   add-people invite; onUserJoined only chimes if a join lands inside that
   window, so it plays for the inviter alone and only for a deliberate add. */
let lastAddInviteAt = 0;
const ADD_SOUND_WINDOW_MS = 60_000;
/** Quieter than the source recording — this is a background confirmation,
 * not something that should cut across the conversation. */
const ADD_SOUND_VOLUME = 0.5;

/** Join the media session and render the call stage into `container`. */
export async function joinSession(
  sessionId: string,
  audioOnly: boolean,
  container: HTMLElement,
  handlers: SessionHandlers
): Promise<void> {
  const CometChatCalls = await loadCalls();
  const me = await CometChat.getLoggedinUser();
  if (!me) throw new Error("Not signed in");
  const { token } = await CometChatCalls.generateToken(sessionId, me.getAuthToken());
  // Fresh call — don't let an add from a previous call still be "recent".
  lastAddInviteAt = 0;

  /* Tracked locally rather than read back off the SDK: onUserLeft fires
     per departure with no "how many are left" figure, and a 1:1 call has
     to know the difference between "the other person hung up" (end now,
     like every other phone) and "one of four people dropped" (carry on). */
  let others = 0;

  const settings = new CometChatCalls.CallSettingsBuilder()
    .enableDefaultLayout(true)
    .setIsAudioOnlyCall(audioOnly)
    .setCallListener(
      new CometChatCalls.OngoingCallListener({
        onCallEnded: handlers.onEnded,
        onCallEndButtonPressed: handlers.onEnded,
        onUserJoined: () => {
          others += 1;
          // Only chime if I just added someone — otherwise a normal 1:1
          // answer or group-call join would play it for everyone.
          if (Date.now() - lastAddInviteAt < ADD_SOUND_WINDOW_MS) {
            sounds.playOnce(sounds.SOUND.personAddedToCall, ADD_SOUND_VOLUME);
          }
        },
        onUserLeft: () => {
          others = Math.max(0, others - 1);
          handlers.onPeerLeft?.(others);
        },
        // The session died on its own (server-side idle/timeout). Without
        // this the SDK tears the media down but the app keeps showing a
        // live call that no longer exists.
        onSessionTimeout: handlers.onEnded,
        onError: (error: any) => handlers.onError(error?.message ?? "Call failed"),
      })
    )
    .build();
  await CometChatCalls.startSession(token, settings, container);
}

export function leaveSession(): void {
  if (!callsModulePromise) return;
  void callsModulePromise.then((CometChatCalls) => {
    try {
      CometChatCalls.endSession();
    } catch {
      // Session was already torn down.
    }
  });
}

/**
 * Direct mute/camera controls — used by the minimized call widget's own
 * small button row (CallManager.tsx), independent of the default layout's
 * own buttons rendered inside the session frame. Those stay exactly as
 * they are; this just gives the minimized view a second, decoupled way to
 * reach the same state, since its own buttons are too cramped to use once
 * the frame is shrunk down.
 */
export function setCallMuted(muted: boolean): void {
  if (!callsModulePromise) return;
  void callsModulePromise.then((CometChatCalls) => {
    try {
      CometChatCalls.muteAudio(muted);
    } catch {
      /* session already ended */
    }
  });
}

export function setCallVideoPaused(paused: boolean): void {
  if (!callsModulePromise) return;
  void callsModulePromise.then((CometChatCalls) => {
    try {
      CometChatCalls.pauseVideo(paused);
    } catch {
      /* session already ended */
    }
  });
}

/** Call history for the Calls tab — lazy-loads the calls SDK just like a
 * real call would, so browsing history doesn't pull in the heavy WebRTC
 * bundle on initial app load. */
export async function fetchCallLogs(limit = 30): Promise<CallLog[]> {
  await loadCalls();
  const { CallLogRequestBuilder } = await import("@cometchat/calls-sdk-javascript");
  const me = await CometChat.getLoggedinUser();
  if (!me) return [];
  const authToken = me.getAuthToken() || "";
  const builder = new CallLogRequestBuilder()
    .setLimit(limit)
    .setCallCategory("call")
    .setAuthToken(authToken);
  return builder.build().fetchNext();
}

/** Tell CometChat the 1:1 call is over (group calls just leave the session). */
export async function endCallRecord(sessionId: string): Promise<void> {
  try {
    await CometChat.endCall(sessionId);
  } catch {
    // The other party may have ended it first.
  }
}

/* ---------- transient-message signalling fallback ----------
   The SDK's own onOutgoingCallAccepted/Rejected events historically never
   reached the caller in this app (confirmed across dev + production, fresh
   accounts, and even the UI Kit's own internal listeners). Messages, however,
   demonstrably deliver in real time both ways — so accept/decline/cancel are
   ALSO signalled over transient messages (real-time only, never stored, no
   unread side-effects) as a redundant path. Whichever arrives first wins. */

export type CallSignalAction = "accepted" | "rejected" | "cancelled";

export function sendCallSignal(
  toId: string,
  toType: "user" | "group",
  action: CallSignalAction,
  sessionId: string
): void {
  try {
    const rType =
      toType === "user" ? CometChat.RECEIVER_TYPE.USER : CometChat.RECEIVER_TYPE.GROUP;
    CometChat.sendTransientMessage(
      new CometChat.TransientMessage(toId, rType, {
        t: "dbm_call_signal",
        action,
        sessionId,
      })
    );
  } catch (e) {
    console.warn("call signal send failed", e);
  }
}

export function parseCallSignal(
  message: CometChat.TransientMessage
): { action: CallSignalAction; sessionId: string } | null {
  const data = message?.getData?.();
  if (!data || data.t !== "dbm_call_signal" || !data.sessionId) return null;
  return { action: data.action, sessionId: data.sessionId };
}

/* ---------- mid-call invites ----------
   "Add people to this call": the inviter sends the ongoing session id over
   a transient message; the invitee joins that session directly via the
   Calls SDK (joinSession works for any session id + own auth token), no
   new CometChat.Call object involved. Transient = real-time only, no
   stored message, no unread side-effects — same transport the
   accept/reject fallback already relies on. */

export type CallInvite = {
  sessionId: string;
  audioOnly: boolean;
  title: string;
  fromName: string;
  /** Optional: absent on invites sent by an older client build. */
  fromUid?: string;
  /** Where the ephemeral in-call chat is broadcast for this call (the
   * group for a group call, otherwise the peer) — passed along so a
   * mid-call joiner posts into the same channel everyone else is on.
   * Optional: older client builds omit it. */
  chatTargetId?: string;
  chatTargetType?: "user" | "group";
};

export function sendCallInvite(toUid: string, invite: CallInvite): void {
  // Mark that I deliberately added someone, so the join chime plays for me
  // (and only me) when they actually arrive — see joinSession's onUserJoined.
  lastAddInviteAt = Date.now();
  try {
    CometChat.sendTransientMessage(
      new CometChat.TransientMessage(toUid, CometChat.RECEIVER_TYPE.USER, {
        t: "dbm_call_invite",
        ...invite,
      })
    );
  } catch (e) {
    console.warn("call invite send failed", e);
  }
}

export function parseCallInvite(message: CometChat.TransientMessage): CallInvite | null {
  const data = message?.getData?.();
  if (!data || data.t !== "dbm_call_invite" || !data.sessionId) return null;
  return {
    sessionId: data.sessionId,
    audioOnly: !!data.audioOnly,
    title: data.title || "Call",
    fromName: data.fromName || "Someone",
    fromUid: data.fromUid || undefined,
    chatTargetId: data.chatTargetId || undefined,
    chatTargetType:
      data.chatTargetType === "group" ? "group" : data.chatTargetType === "user" ? "user" : undefined,
  };
}

/* ---------- ephemeral in-call chat ----------
   A lightweight text side-channel that exists ONLY for the life of a
   call. It rides the same transient-message transport as call signalling
   (real-time only, never stored, no unread side-effects), so the moment
   the call ends and the stage unmounts, the whole conversation is gone —
   deliberately never written to persistent history. That keeps an in-call
   aside out of the real chat log, and sidesteps the "which conversation
   does a group call's chat even belong to" problem entirely (it belongs
   to nothing — it's throwaway).

   Routing mirrors the call itself: a group call broadcasts to the group
   (every participant is a member); a 1:1 call sends to the peer. */

export type CallChatMessage = {
  sessionId: string;
  text: string;
  fromName: string;
  fromUid: string;
  ts: number;
};

export function sendCallChat(
  toId: string,
  toType: "user" | "group",
  msg: CallChatMessage
): void {
  try {
    const rType =
      toType === "user" ? CometChat.RECEIVER_TYPE.USER : CometChat.RECEIVER_TYPE.GROUP;
    CometChat.sendTransientMessage(
      new CometChat.TransientMessage(toId, rType, { t: "dbm_call_chat", ...msg })
    );
  } catch (e) {
    console.warn("call chat send failed", e);
  }
}

export function parseCallChat(message: CometChat.TransientMessage): CallChatMessage | null {
  const data = message?.getData?.();
  if (!data || data.t !== "dbm_call_chat" || !data.sessionId || typeof data.text !== "string") {
    return null;
  }
  return {
    sessionId: data.sessionId,
    text: data.text,
    fromName: data.fromName || "Someone",
    fromUid: data.fromUid || "",
    ts: typeof data.ts === "number" ? data.ts : Date.now(),
  };
}

/* ---------- ring tones ----------
   WebAudio instead of <audio> elements: no asset loading, no play()/pause()
   races (the UI Kit's own ringtone audio previously threw unhandled errors).
   If the browser's autoplay policy blocks it (no prior user gesture on the
   callee's page), it degrades silently. */

let audioCtx: AudioContext | null = null;
let ringInterval: number | null = null;

function ensureCtx(): AudioContext | null {
  try {
    if (!audioCtx) {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      audioCtx = new Ctx();
    }
    if (audioCtx.state === "suspended") void audioCtx.resume();
    return audioCtx;
  } catch {
    return null;
  }
}

function beep(ctx: AudioContext, freq: number, at: number, dur: number, gainPeak: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(gainPeak, at + 0.02);
  gain.gain.linearRampToValueAtTime(0, at + dur);
  osc.connect(gain).connect(ctx.destination);
  osc.start(at);
  osc.stop(at + dur + 0.05);
}

/** Short two-note chime for a new chat message — distinct from the ring
 * tones above, and much shorter (no interval, plays once). Same
 * autoplay-policy caveat as startRing: silently does nothing without a
 * prior user gesture on the page. */
/** Two-tone descending "hung up" sound — synthesized because no suitable
 * free hangup sound effect was found to use as a real audio file, unlike
 * every other sound in the app (see sounds.ts). Plays once when a call
 * ends, regardless of who ended it. */
export function playHangupTone(): void {
  const ctx = ensureCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    beep(ctx, 480, now, 0.16, 0.4);
    beep(ctx, 340, now + 0.14, 0.22, 0.4);
  } catch {
    /* ignore */
  }
}

export function startRing(kind: "incoming" | "outgoing"): void {
  stopRing();
  const ctx = ensureCtx();
  if (!ctx) return;
  const play = () => {
    try {
      const now = ctx.currentTime;
      if (kind === "incoming") {
        // Double chirp, like a phone ring.
        beep(ctx, 880, now, 0.18, 0.12);
        beep(ctx, 880, now + 0.26, 0.18, 0.12);
      } else {
        // Single soft ringback.
        beep(ctx, 440, now, 0.5, 0.05);
      }
    } catch {
      /* ignore */
    }
  };
  play();
  ringInterval = window.setInterval(play, kind === "incoming" ? 2200 : 3000);
}

export function stopRing(): void {
  if (ringInterval !== null) {
    window.clearInterval(ringInterval);
    ringInterval = null;
  }
}
